import { useUserDirectory } from "@/hooks/use-user-directory";
import { ConvertFileToPdfSlideover } from "@/components/features/matter/file-repo/ConvertFileToPdfSlideover";
import { DuplicateFileSlideover } from "@/components/features/matter/file-repo/DuplicateFileSlideover";
import { EditDocxFileSlideover } from "@/components/features/matter/file-repo/EditDocxFileSlideover";
import { EditPdfFileSlideover } from "@/components/features/matter/file-repo/EditPdfFileSlideover";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { AddOrEditFirmFileModal } from "@/components/features/settings/firm/AddOrEditFirmFileModal";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import {
  Button,
  Form,
  FormSubmitButton,
  LoadingSpinner,
  MultiselectFormField,
  SearchableSelectFormField,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveFileOptions } from "@/lib/data/react-query/files/canonical-files";
import { adaptFile } from "@/lib/data/requests/files/file-adapter";
import {
  retrieveFirmFilesOptions,
  useCreateMatterUserConnection,
  useDeleteMatterUserConnection,
} from "@/lib/data/react-query";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterFile,
  useDeleteMatterFile,
  useGenerateMatterFilePdf,
  retrieveMatterFilesQueryOptions,
  useUpdateMatterFile,
} from "@/lib/data/react-query/matters/matter-file";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { MatterFileSchema } from "@/lib/data/schemas";
import {
  acceptedFileMimeTypes,
  acceptedFileMimeTypesImageOnly,
} from "@/lib/data/static/fileStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterFile } from "@/lib/types";
import { getFirmFileDisplayName } from "@/lib/utils/firmFile";
import { formatUsersName } from "@/lib/utils/format";
import {
  ensureFileNameHasExtension,
  getFileNameParts,
  getFileDocumentFormat,
  getMatterFileNamesInFolder,
  matterFileNameExistsInFolder,
  MATTER_FILE_TAG_OPTIONS,
} from "@/lib/utils/matterFile";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Upload, X as XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

interface AddOrEditFileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing: boolean;
  activeFile?: MatterFile | undefined;
  activeFileId?: string;
  matterId?: string;
  massTortId?: string;
  currentFolderId: string;
  isFolder: boolean;
  handleDeleteItem?: () => void;
  isCriticalImage?: boolean;
  autoOpenUpload?: boolean;
  initialFile?: File;
  afterCreateFile?: (file: MatterFile) => Promise<void> | void;
  showPreview?: boolean;
}

export const baseMatterFileFormSchema = MatterFileSchema.pick({
  name: true,
  description: true,
  tags: true,
}).extend({
  messageField: z.string().optional(),
  matterFileReviewerId: z.string().optional(),
});

export const getMatterFileFormSchema = () => baseMatterFileFormSchema;

const defaultFormValues = {
  name: "",
  description: "",
  tags: [],
  matterFileReviewerId: "",
};

export const AddOrEditFileModal = ({
  open,
  setOpen,
  editing,
  activeFileId,
  matterId,
  massTortId,
  currentFolderId,
  isFolder,
  handleDeleteItem,
  isCriticalImage,
  autoOpenUpload,
  initialFile,
  afterCreateFile,
  showPreview = true,
}: AddOrEditFileModalProps) => {
  const parentInput = massTortId
    ? { massTortId }
    : { matterId: matterId ?? "" };
  const isMassTortFile = Boolean(massTortId);

  const {
    data: matterData,
    isLoading: isMatterLoading,
    error: matterError,
  } = useQuery({
    ...retrieveMatterQueryOptions(matterId ?? ""),
    enabled: open && Boolean(matterId) && !isMassTortFile,
  });
  const {
    data: massTortData,
    isLoading: isMassTortLoading,
    error: massTortError,
  } = useQuery({
    ...retrieveMassTortQueryOptions(massTortId ?? ""),
    enabled: open && Boolean(massTortId),
  });
  const { data: massTortFilesData, isLoading: isMassTortFilesLoading } =
    useQuery({
      ...retrieveMatterFilesQueryOptions({ massTortId: massTortId ?? "" }),
      enabled: open && Boolean(massTortId),
    });
  const currentFile = useQuery(
    retrieveFileOptions(activeFileId ?? "", open && editing),
  );

  const matterFileFormSchema = getMatterFileFormSchema();

  const matter = matterData?.data;
  const massTort = massTortData?.data;
  const fileParent = massTort ?? matter;
  const parentFiles: MatterFile[] = (
    massTortFilesData?.data ??
    (isMassTortFile ? massTort?.files : matter?.files) ??
    []
  ).map((parentFile) => ({
    ...parentFile,
    matterId: parentFile.matterId ?? matterId ?? massTortId ?? "",
    idLocation: parentFile.idLocation ?? "",
    presignedUrlKey: parentFile.presignedUrlKey ?? undefined,
    variables: parentFile.variables ?? undefined,
  })) as MatterFile[];
  const isParentLoading = isMassTortFile
    ? isMassTortLoading || isMassTortFilesLoading
    : isMatterLoading;
  const parentError =
    currentFile.error ?? (isMassTortFile ? massTortError : matterError);
  const activeFileMetadata = activeFileId
    ? parentFiles.find((file) => file.id === activeFileId)
    : undefined;
  const activeFile =
    activeFileMetadata && currentFile.data
      ? adaptFile<MatterFile>(currentFile.data, activeFileMetadata)
      : activeFileMetadata;
  const currentReviewerConnection =
    activeFile && matter
      ? matter.userConnections.find(
          (connection) =>
            connection.label === "Reviewer" &&
            connection.fileId === activeFile.id,
        )
      : undefined;
  const [file, setFile] = useState<{ file: File; url: string } | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoOpenUploadHandledRef = useRef(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [isConvertToPdfOpen, setIsConvertToPdfOpen] = useState(false);
  const [isDocxEditorOpen, setIsDocxEditorOpen] = useState(false);
  const [isPdfEditorOpen, setIsPdfEditorOpen] = useState(false);
  const [isCreateFirmFileOpen, setIsCreateFirmFileOpen] = useState(false);
  const [isSavingToFirmFile, setIsSavingToFirmFile] = useState(false);
  const [prefillFirmFile, setPrefillFirmFile] = useState<
    { file: File; url: string } | undefined
  >();
  const [prefillFirmFileValues, setPrefillFirmFileValues] = useState<{
    name?: string;
    description?: string;
  }>({});
  const form = useForm<z.infer<typeof matterFileFormSchema>>({
    resolver: zodResolver(matterFileFormSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...activeFile,
          name: getFileNameParts(activeFile?.name).fileName,
          matterFileReviewerId: currentReviewerConnection?.userId ?? "",
        }
      : {
          ...defaultFormValues,
          name: getFileNameParts(initialFile?.name).fileName,
        },
    mode: "onSubmit",
  });
  const userDirectory = useUserDirectory({
    enabled: open && editing && !isFolder && Boolean(matter),
    selectedIds: [form.watch("matterFileReviewerId") ?? ""],
  });
  const {
    users,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = userDirectory;

  const { hasActionPermission } = useUser();
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.delete,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDocumentEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const canManageFileReviewer = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.update,
  );
  const canCreateFirmFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateMatterFiles = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterFile,
    ACTION_PERMISSION_VALUES.create,
  );
  const canViewFirmFiles = Boolean(true);

  const createMatterFile = useCreateMatterFile();
  const updateMatterFile = useUpdateMatterFile();
  const deleteMatterFile = useDeleteMatterFile();
  const generateMatterFilePdf = useGenerateMatterFilePdf();
  const createMatterUserConnection = useCreateMatterUserConnection({
    showToast: false,
  });
  const deleteMatterUserConnection = useDeleteMatterUserConnection({
    showToast: false,
  });
  const { data: firmFilesData } = useQuery(
    retrieveFirmFilesOptions(open && editing && !isFolder && canViewFirmFiles),
  );
  const firmFiles = firmFilesData?.data ?? [];
  const requireFileParent = () => {
    if (!fileParent) {
      throw new Error("File parent data is not loaded");
    }

    return fileParent;
  };

  // Delete an item
  const defaultHandleDeleteItem = async () => {
    if (!activeFile) return;
    requireFileParent();

    await deleteMatterFile.mutateAsync({
      ...parentInput,
      id: activeFile.id,
    });
    setOpen(false);
  };

  const localHandleDeleteItem = handleDeleteItem ?? defaultHandleDeleteItem;

  // Handle saving edits
  const handleSaveEdit = async (
    data: z.infer<typeof baseMatterFileFormSchema>,
  ) => {
    if (!activeFile) return;
    requireFileParent();
    const fileName = isFolder
      ? data.name.trim()
      : ensureFileNameHasExtension(
          data.name,
          getFileNameParts(activeFile.name).fileExtension,
        );
    if (
      matterFileNameExistsInFolder(
        parentFiles,
        activeFile.idLocation || "",
        fileName,
        activeFile.id,
      )
    ) {
      form.setError("name", {
        type: "manual",
        message: "A file with this name already exists",
      });
      return;
    }

    if (editing) {
      const description = data.description?.trim();
      await updateMatterFile.mutateAsync({
        ...parentInput,
        id: activeFile.id,
        revision: activeFile.revision,
        dateModified: activeFile.dateModified,
        idLocation: activeFile.idLocation,
        name: fileName,
        tags: data.tags && data.tags.length > 0 ? data.tags : undefined,
        ...(description ? { description } : {}),
      });
      const selectedReviewerId = data.matterFileReviewerId;
      const reviewerChanged =
        (selectedReviewerId ?? "") !== currentReviewerConnection?.userId;

      if (matter && reviewerChanged && canManageFileReviewer) {
        if (currentReviewerConnection) {
          await deleteMatterUserConnection.mutateAsync({
            matterId: matter.id,
            id: currentReviewerConnection.id,
          });
        }

        if (selectedReviewerId) {
          const selectedUser = users.find(
            (user) => user.id === selectedReviewerId,
          );
          if (selectedUser) {
            await createMatterUserConnection.mutateAsync({
              matterId: matter.id,
              fileId: activeFile.id,
              label: "Reviewer",
              userId: selectedUser.id,
            });
          }
        }
      }
      setOpen(false);
    }

    form.reset();
  };

  // Create a new folder
  const handleCreateFolder = async (
    data: z.infer<typeof baseMatterFileFormSchema>,
  ) => {
    requireFileParent();
    const description = data.description?.trim();

    await createMatterFile.mutateAsync({
      ...parentInput,
      isFolder: true,
      idLocation: currentFolderId,
      name: data.name.trim(),
      tags: data.tags && data.tags.length > 0 ? data.tags : undefined,
      ...(description ? { description } : {}),
    });
    setOpen(false);
    form.reset();
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const fileNameParts = getFileNameParts(file.name);
    if (!form.getValues("name")) {
      form.setValue("name", fileNameParts.fileName, {
        shouldDirty: true,
      });
    }
    const objectUrl = URL.createObjectURL(file);
    const uploadedFile = { file, url: objectUrl };
    setFile(uploadedFile);
    handleAutoSaveAfterUpload(uploadedFile);
  };

  const handleRemoveFileUpload = () => {
    setFile(undefined);
  };

  const createFile = async (
    data: z.infer<typeof baseMatterFileFormSchema>,
    uploadedFileOverride?: { file: File; url: string },
  ) => {
    requireFileParent();
    const uploadedFile = uploadedFileOverride ?? file;
    if (!uploadedFile) {
      toast.error("Please upload a file");
      return;
    }
    const fileExtension = getFileNameParts(
      uploadedFile.file.name,
    ).fileExtension;
    const fullFileName = ensureFileNameHasExtension(data.name, fileExtension);

    const description = data.description?.trim();
    const result = await createMatterFile.mutateAsync({
      ...parentInput,
      isFolder: false,
      idLocation: currentFolderId,
      name: fullFileName,
      tags: data.tags && data.tags.length > 0 ? data.tags : undefined,
      ...(description ? { description } : {}),
      file: uploadedFile.file,
    });

    // Call the callback with the newly created file if provided
    if (afterCreateFile) {
      await afterCreateFile(result.data);
    }

    setOpen(false);
  };

  const handleAutoSaveAfterUpload = (uploadedFile: {
    file: File;
    url: string;
  }) => {
    if (!autoOpenUpload || editing || isFolder) return;

    void form
      .handleSubmit((data) => createFile(data, uploadedFile))()
      .catch(() => undefined);
  };

  // fixes type issue with form.handleSubmit
  const handleCreateFileSubmit = async (
    data: z.infer<typeof baseMatterFileFormSchema>,
  ) => {
    await createFile(data);
  };

  const itemTitle = isFolder ? "folder" : "file";
  const isActiveDocx =
    !!activeFile && getFileDocumentFormat(activeFile) === "docx";
  const isActivePdf =
    !!activeFile && getFileDocumentFormat(activeFile) === "pdf";
  const canEditDocx = Boolean(
    matter &&
      editing &&
      isActiveDocx &&
      hasUpdatePermission &&
      hasDocumentEditPermission,
  );
  const canEditPdf = Boolean(
    matter &&
      editing &&
      isActivePdf &&
      hasUpdatePermission &&
      hasDocumentEditPermission,
  );
  const canConvertToPdf = Boolean(
    editing && activeFile && isActiveDocx && canCreateMatterFiles,
  );
  const reviewerFormValue = form.watch("matterFileReviewerId");
  const reviewerFieldHasValue = Boolean(reviewerFormValue);

  const isReviewerMutationPending =
    createMatterUserConnection.isPending ||
    deleteMatterUserConnection.isPending;
  const isFormLoading =
    createMatterFile.isPending ||
    updateMatterFile.isPending ||
    deleteMatterFile.isPending ||
    generateMatterFilePdf.isPending ||
    isReviewerMutationPending ||
    isSavingToFirmFile;

  const buildUniqueFirmFileName = (baseName: string) => {
    if (!canViewFirmFiles) {
      return baseName;
    }
    const existingNames = new Set(
      firmFiles.map((file) => getFirmFileDisplayName(file).toLowerCase()),
    );
    if (!existingNames.has(baseName.toLowerCase())) {
      return baseName;
    }
    const { fileName, fileExtension } = getFileNameParts(baseName);
    let index = 1;
    let candidate = `${fileName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;
    while (existingNames.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${fileName} (${index})${fileExtension ? `.${fileExtension}` : ""}`;
    }
    return candidate;
  };

  const handleSaveAsFirmFile = async () => {
    if (!activeFile) return;
    if (!activeFile.presignedUrlKey) {
      toast.error("This file is not available to save as a firm file.");
      return;
    }
    if (isSavingToFirmFile) return;

    setIsSavingToFirmFile(true);
    try {
      const presigned = await generateS3ItemPreSignedUrlV1("matter-file")({
        clientMethod: "get_object",
        key: activeFile.presignedUrlKey,
      });
      const response = await fetch(presigned.data.presignedUrl, {
        method: "GET",
      });
      if (!response.ok) {
        toast.error("Unable to download this file. Please try again.");
        return;
      }
      const blob = await response.blob();
      const contentType =
        response.headers.get("content-type") || blob.type || undefined;
      const desiredBaseName = form.getValues("name").trim();
      const fallbackBaseName = getFileNameParts(activeFile.name).fileName;
      const extension = getFileNameParts(activeFile.name).fileExtension;
      const fullName = extension
        ? `${desiredBaseName || fallbackBaseName}.${extension}`
        : desiredBaseName || fallbackBaseName || activeFile.name;
      const firmFileName = buildUniqueFirmFileName(fullName);
      const file = new File([blob], firmFileName, {
        type: contentType,
      });
      const previewUrl = URL.createObjectURL(file);
      setPrefillFirmFile({ file, url: previewUrl });
      setPrefillFirmFileValues({
        name: getFileNameParts(firmFileName).fileName,
        description: activeFile.description ?? "",
      });
      setIsCreateFirmFileOpen(true);
    } catch (error) {
      console.error("Failed to save matter file as firm file", error);
      toast.error("Unable to save as a firm file. Please try again.");
    } finally {
      setIsSavingToFirmFile(false);
    }
  };

  useEffect(() => {
    return () => {
      if (prefillFirmFile?.url) {
        URL.revokeObjectURL(prefillFirmFile.url);
      }
    };
  }, [prefillFirmFile]);

  useEffect(() => {
    if (!open || editing || isFolder || !initialFile) return;

    const objectUrl = URL.createObjectURL(initialFile);
    setFile({ file: initialFile, url: objectUrl });

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [editing, initialFile, isFolder, open]);

  useEffect(() => {
    if (!open) {
      setFile(undefined);
      form.reset(defaultFormValues);
      setIsDuplicateOpen(false);
      setIsConvertToPdfOpen(false);
      setIsDocxEditorOpen(false);
      setIsPdfEditorOpen(false);
      setIsSavingToFirmFile(false);
      setIsCreateFirmFileOpen(false);
      setPrefillFirmFile(undefined);
      setPrefillFirmFileValues({});
    }
  }, [open, form]);

  useEffect(() => {
    if (!open) return;
    form.setValue(
      "matterFileReviewerId",
      currentReviewerConnection?.userId ?? "",
      {
        shouldDirty: false,
      },
    );
  }, [currentReviewerConnection?.userId, form, open]);

  // Automatically trigger the upload file input when opening a new file slideover,
  // if requested by the caller (e.g. from the File Repository "New File" button).
  useEffect(() => {
    if (!open || editing || isFolder) {
      autoOpenUploadHandledRef.current = false;
      return;
    }

    if (!autoOpenUpload) {
      autoOpenUploadHandledRef.current = false;
      return;
    }

    if (isParentLoading || !fileParent) {
      return;
    }

    if (file) return;
    if (autoOpenUploadHandledRef.current) return;

    autoOpenUploadHandledRef.current = true;

    const timeoutId = window.setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);

    return () => {
      window.clearTimeout(timeoutId);
      autoOpenUploadHandledRef.current = false;
    };
  }, [
    open,
    editing,
    isFolder,
    autoOpenUpload,
    file,
    isParentLoading,
    fileParent,
  ]);

  if (activeFile && matter && isActiveDocx && isDocxEditorOpen) {
    return (
      <EditDocxFileSlideover
        open={isDocxEditorOpen}
        setOpen={setIsDocxEditorOpen}
        matterId={matter.id}
        sourceFile={activeFile}
        onSaved={() => {
          setOpen(false);
        }}
      />
    );
  }

  if (activeFile && matter && isActivePdf && isPdfEditorOpen) {
    return (
      <EditPdfFileSlideover
        open={isPdfEditorOpen}
        setOpen={setIsPdfEditorOpen}
        matterId={matter.id}
        sourceFile={activeFile}
        onSaved={() => {
          setOpen(false);
        }}
      />
    );
  }

  const title = editing
    ? `Edit ${itemTitle}`
    : autoOpenUpload && !isFolder
      ? "Upload file"
      : `New ${itemTitle}`;
  const description = `Make changes to the ${activeFile?.isFolder ? "folder" : "file"} details.`;

  const shellContent = (
    <>
      {(isParentLoading && !fileParent) ||
      (editing && currentFile.isFetching) ? (
        <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-4">
          <LoadingSpinner className="h-6 w-6" />
        </div>
      ) : parentError || !fileParent || (editing && !currentFile.data) ? (
        <div className="flex min-h-0 flex-1 items-center justify-center px-6 py-4">
          <Typography className="text-destructive text-sm">
            Error loading file data
          </Typography>
        </div>
      ) : (
        <Form {...form}>
          <form
            className="flex grow flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (editing) {
                await form.handleSubmit(handleSaveEdit)();
              } else {
                if (isFolder) {
                  await form.handleSubmit(handleCreateFolder)();
                } else {
                  await form.handleSubmit(handleCreateFileSubmit)();
                }
              }
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            <div className="overflow-y-auto">
              <fieldset
                disabled={editing ? !hasUpdatePermission : false}
                className={slideoverFieldsetClassName}
              >
                <div className="flex flex-col gap-y-6">
                  <div className="flex items-end gap-3">
                    <div className="grow">
                      <TextFormField
                        register={form.register("name")}
                        label="Name*"
                      />
                    </div>
                    {!isFolder && (
                      <Typography
                        variant="subHeading"
                        className="mb-3 whitespace-nowrap"
                      >
                        {editing &&
                          `.${getFileNameParts(activeFile?.name).fileExtension}`}
                        {!editing &&
                          file &&
                          `.${getFileNameParts(file.file.name).fileExtension}`}
                      </Typography>
                    )}
                  </div>
                  {!isFolder && !editing && (
                    <div>
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload file
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={(e) => {
                          handleFileUpload(e.target.files);
                        }}
                        accept={
                          isCriticalImage
                            ? acceptedFileMimeTypesImageOnly.join(",")
                            : acceptedFileMimeTypes.join(",")
                        }
                      />
                    </div>
                  )}
                </div>

                {!isFolder && (
                  <>
                    {showPreview && (activeFile || file) && (
                      <div className="col-span-full">
                        <>
                          {file && (
                            <FileViewer
                              file={
                                activeFile
                                  ? {
                                      fileName: activeFile.name,
                                      fileId: activeFile.id,
                                      presignedURLKey:
                                        activeFile.presignedUrlKey || "",
                                    }
                                  : {
                                      file: file.file,
                                      url: file.url,
                                    }
                              }
                              fileType="matter-file"
                              showDownload={false}
                              handleRemoveFileUpload={handleRemoveFileUpload}
                              compactDocxPreview
                              viewerClassName="min-h-0"
                              imageClassName="max-h-[400px] w-auto"
                            />
                          )}
                          {activeFile && (
                            <FileViewer
                              file={{
                                fileName: activeFile.name,
                                fileId: activeFile.id,
                                presignedURLKey:
                                  activeFile.presignedUrlKey || "",
                              }}
                              fileType="matter-file"
                              compactDocxPreview
                              viewerClassName="min-h-0"
                              imageClassName="max-h-[400px] w-auto"
                              onEdit={
                                canEditDocx || canEditPdf
                                  ? () => {
                                      if (canEditDocx) {
                                        setIsDocxEditorOpen(true);
                                        return;
                                      }
                                      if (canEditPdf) {
                                        setIsPdfEditorOpen(true);
                                      }
                                    }
                                  : undefined
                              }
                            />
                          )}
                        </>
                      </div>
                    )}
                    <div className="col-span-2 grid grid-cols-subgrid">
                      <div className="col-span-1">
                        <MultiselectFormField
                          label="File tags"
                          options={MATTER_FILE_TAG_OPTIONS}
                          placeholder="Select file tags"
                          register={form.register("tags")}
                          maxCount={3}
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="col-span-2">
                  <TextAreaFormField
                    register={form.register("description")}
                    label="Description"
                  />
                </div>
                {matter && !isFolder && editing && activeFile && (
                  <div className="relative col-span-1">
                    <SearchableSelectFormField
                      items={users}
                      {...userDirectory.selectProps}
                      itemsKey="id"
                      formatItemLabel={(user) => formatUsersName(user) ?? ""}
                      formatSelectedItemLabel={(user) =>
                        formatUsersName(user) ?? ""
                      }
                      placeholder="Select reviewer"
                      label="Reviewer"
                      register={form.register("matterFileReviewerId")}
                      isItemsLoading={isUsersLoading}
                      isItemsError={isUsersError}
                      disabled={
                        !hasUpdatePermission ||
                        !canManageFileReviewer ||
                        isFormLoading
                      }
                      buttonClassName="pr-10"
                    />
                    {reviewerFieldHasValue && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="bg-card/60 absolute top-1/2 right-2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full p-0"
                        style={{ marginTop: 11 }}
                        onClick={() => {
                          form.setValue("matterFileReviewerId", "", {
                            shouldDirty: true,
                          });
                        }}
                        disabled={
                          !hasUpdatePermission ||
                          !canManageFileReviewer ||
                          isFormLoading ||
                          !reviewerFieldHasValue
                        }
                        title="Clear selected reviewer"
                        aria-label="Clear selected reviewer"
                      >
                        <XIcon className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
                {!isFolder && <></>}
              </fieldset>
            </div>
            <div className="flex grow" />
            <div className="border-border bg-card flex shrink-0 items-center gap-2 border-t px-5 py-3">
              {hasDeletePermission && editing && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    localHandleDeleteItem();
                  }}
                >
                  Delete
                </Button>
              )}
              <div className="flex grow justify-end gap-x-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {editing && !isFolder && activeFile && canCreateFirmFiles && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      void handleSaveAsFirmFile();
                    }}
                    disabled={isFormLoading}
                  >
                    {isSavingToFirmFile && (
                      <LoadingSpinner className="mr-2 h-4 w-4" />
                    )}
                    Add to firm files
                  </Button>
                )}
                {canConvertToPdf && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsConvertToPdfOpen(true);
                    }}
                    disabled={isFormLoading}
                  >
                    Convert to PDF
                  </Button>
                )}
                {matter &&
                  editing &&
                  !isFolder &&
                  activeFile &&
                  hasUpdatePermission && (
                    <Button
                      type="button"
                      variant="light-primary"
                      onClick={() => {
                        setIsDuplicateOpen(true);
                      }}
                    >
                      Duplicate
                    </Button>
                  )}
                <FormSubmitButton
                  loading={isFormLoading}
                  disabled={editing && !hasUpdatePermission}
                />
              </div>
            </div>
          </form>
        </Form>
      )}
    </>
  );

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        size="2xl"
        overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
        className="z-[109] w-[calc(100vw-2rem)] max-w-4xl"
        title={title}
        description={description}
        loading={isFormLoading}
        isDirty={form.formState.isDirty}
      >
        {shellContent}
      </AppModal>
      {activeFile && matter ? (
        <DuplicateFileSlideover
          open={isDuplicateOpen}
          setOpen={setIsDuplicateOpen}
          matterId={matter.id}
          sourceFile={activeFile}
          overlayClassName="z-[110] bg-black/50 backdrop-blur-md"
          className="z-[111]"
          onDuplicated={() => {
            setOpen(false);
          }}
        />
      ) : null}
      {activeFile && (
        <ConvertFileToPdfSlideover
          open={isConvertToPdfOpen}
          setOpen={setIsConvertToPdfOpen}
          sourceFileName={activeFile.name}
          existingFileNames={getMatterFileNamesInFolder(
            parentFiles,
            activeFile.idLocation || "",
          )}
          loading={generateMatterFilePdf.isPending}
          overlayClassName="z-[110] bg-black/50 backdrop-blur-md"
          className="z-[111]"
          onConvert={async (matterFileName) => {
            await generateMatterFilePdf.mutateAsync({
              ...parentInput,
              matterFileId: activeFile.id,
              matterFileName,
            });
            setOpen(false);
          }}
        />
      )}
      {canCreateFirmFiles && (
        <AddOrEditFirmFileModal
          open={isCreateFirmFileOpen}
          setOpen={(nextOpen) => {
            if (!nextOpen) {
              setIsCreateFirmFileOpen(false);
              setPrefillFirmFile(undefined);
              setPrefillFirmFileValues({});
              return;
            }
            setIsCreateFirmFileOpen(true);
          }}
          overlayClassName="z-[110] bg-black/50 backdrop-blur-md"
          className="z-[111]"
          prefillFile={prefillFirmFile}
          prefillValues={prefillFirmFileValues}
        />
      )}
    </>
  );
};
